import { useEffect, useRef, useState } from 'react';
import { clearScribeThread, getScribeThread, sendScribe, type ScribeTurn } from '../api.js';

/**
 * The scribe — a chat for putting things INTO the app that are not tasks:
 * "Joey's birthday is Aug 11 2002, add it." Small on purpose; it takes
 * dictation and echoes exactly what it wrote. The coach is for thinking.
 */

interface Turn extends ScribeTurn {
  error?: boolean;
}

/**
 * The scribe conversation itself: thread, hint, composer. Rendered inside the
 * Capture sheet's "Chat" mode (spec 2026-10-09 §5) and by the legacy floating
 * panel below. Loads the stored thread on mount.
 */
export function ScribeChat({ className = 'scribe' }: { className?: string }): JSX.Element {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    let alive = true;
    void getScribeThread()
      .then((t) => {
        if (alive) setTurns(t);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    logRef.current?.scrollTo?.({ top: logRef.current.scrollHeight });
  }, [turns]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    const next: Turn[] = [...turns, { role: 'user', content: text }];
    setTurns(next);
    setDraft('');
    setBusy(true);
    try {
      const r = await sendScribe(text);
      setTurns([...next, { role: 'assistant', content: r.reply, actions: r.actions }]);
    } catch (e) {
      setTurns([
        ...next,
        { role: 'assistant', content: e instanceof Error ? e.message : 'Failed', error: true },
      ]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className={className} role="group" aria-label="Scribe">
      <div className="scribe__head">
        <span className="scribe__title">Scribe</span>
        <span className="scribe__sub">birthdays · events · reminders</span>
        {turns.length > 0 && (
          <button
            className="linkbtn"
            onClick={() => {
              setTurns([]);
              void clearScribeThread().catch(() => undefined);
            }}
          >
            Clear
          </button>
        )}
      </div>
      <div className="scribe__log" ref={logRef}>
        {turns.length === 0 && (
          <div className="scribe__hint">
            Tell me something to record — “my buddy Joey’s birthday is Aug 11 2002” or “remind me
            to renew the car tags on the 30th”.
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`scribe__msg scribe__msg--${t.role}${t.error ? ' scribe__msg--error' : ''}`}>
            <div>{t.content}</div>
            {t.actions && t.actions.length > 0 && (
              <ul className="scribe__actions">
                {t.actions.map((a, j) => (
                  <li key={j}>✓ {a.summary}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {busy && <div className="scribe__msg scribe__msg--assistant scribe__msg--busy">…</div>}
      </div>
      <form
        className="scribe__form"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          ref={inputRef}
          value={draft}
          placeholder="Tell the scribe…"
          aria-label="Message the scribe"
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button className="btn" type="submit" disabled={busy || !draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}

/**
 * Legacy floating scribe (a corner "+" that opens the chat). No longer
 * mounted by Layout — the Capture sheet's Chat mode replaced it — but kept so
 * it can be restored with one line.
 */
export default function Scribe(): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className={`scribe-fab${open ? ' scribe-fab--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close scribe' : 'Add something'}
        title="Add something"
      >
        {open ? '×' : '+'}
      </button>
      {open && (
        <div className="scribe scribe--floating" role="dialog" aria-label="Scribe">
          <ScribeChat className="scribe__inner" />
        </div>
      )}
    </>
  );
}
